import { useEffect, useMemo, useRef, useCallback } from 'react';
import { PenLine } from 'lucide-react';
import { AutocompleteSelect } from '@/Components/shared/AutocompleteSelect';
import { useContractDrafts } from '@/features/delivery-proof/hooks/useContracts';
import type { ContractListItem, ContractDraft, ContractListResponse } from '@/features/contracts/types/contract.types';

/** Sélection = contrat finalisé OU brouillon */
export type ContractSelection =
  | { type: 'contract'; contract: ContractListItem }
  | { type: 'draft'; draft: ContractDraft };

/** Type unifié pour l'autocomplete : contrats et brouillons dans la même liste */
type SelectableItem =
  | { kind: 'contract'; data: ContractListItem }
  | { kind: 'draft'; data: ContractDraft };

interface ContractSelectorProps {
  value: ContractSelection | null;
  onChange: (selection: ContractSelection | null) => void;
  error?: string;
  societyId?: string;
  size?: 'default' | 'sm';
  initialContractId?: number;
  tooltip?: string;
}

const getItemId = (item: SelectableItem): string =>
  item.kind === 'contract' ? `contract-${item.data.id}` : `draft-${item.data.uuid}`;

const getItemLabel = (item: SelectableItem): string => {
  if (item.kind === 'contract') {
    const c = item.data;
    const parts: string[] = [];
    if (c.beneficiary) parts.push(`${c.beneficiary.firstName} ${c.beneficiary.lastName}`);
    if (c.vehicle?.registration) parts.push(c.vehicle.registration);
    if (c.vehicle?.brand || c.vehicle?.model) parts.push([c.vehicle.brand, c.vehicle.model].filter(Boolean).join(' '));
    return parts.length > 0 ? parts.join(' - ') : c.label;
  }
  return `Brouillon : ${item.data.label || 'Sans nom'}`;
};

/**
 * Composant de sélection de contrat/brouillon basé sur AutocompleteSelect
 */
export const ContractSelector = ({ value, onChange, error, societyId, size = 'default', initialContractId, tooltip }: ContractSelectorProps) => {
  const { data: drafts } = useContractDrafts();
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // Filtrer les brouillons par societyId
  const filteredDrafts = useMemo(() => {
    const allDrafts = drafts || [];
    return societyId ? allDrafts.filter((d) => d.societyId === societyId) : allDrafts;
  }, [drafts, societyId]);

  // Valeur courante pour l'autocomplete
  const currentValue = useMemo(() => {
    if (!value) return null;
    return value.type === 'contract' ? `contract-${value.contract.id}` : `draft-${value.draft.uuid}`;
  }, [value]);

  // Transform la réponse API /pro-v2/contracts en format nodes + brouillons fusionnés
  const transformResponse = useCallback(
    (data: ContractListResponse) => {
      const contractItems: SelectableItem[] = (data.contracts || []).map((c) => ({ kind: 'contract' as const, data: c }));
      const draftItems: SelectableItem[] = filteredDrafts.map((d) => ({ kind: 'draft' as const, data: d }));
      return {
        nodes: [...contractItems, ...draftItems],
        pageInfos: { hasNextPage: false },
      };
    },
    [filteredDrafts],
  );

  // Auto-sélection du contrat initial (depuis URL param)
  useEffect(() => {
    if (initialContractId && !value) {
      // L'auto-sélection sera gérée par AutocompleteSelect via la résolution de valeur initiale
    }
  }, [initialContractId, value]);

  const handleChange = useCallback(
    (id: string | null, item: SelectableItem | null) => {
      if (!item || !id) {
        onChangeRef.current(null);
        return;
      }
      if (item.kind === 'contract') {
        onChangeRef.current({ type: 'contract', contract: item.data });
      } else {
        onChangeRef.current({ type: 'draft', draft: item.data });
      }
    },
    [],
  );

  return (
    <div>
    <AutocompleteSelect<SelectableItem>
      apiEndpoint="/pro-v2/contracts"
      searchParam="search"
      getOptionLabel={getItemLabel}
      getOptionValue={getItemId}
      value={currentValue}
      onChange={handleChange}
      label="Contrat"
      placeholder="Sélectionnez un contrat"
      size={size}
      tooltip={tooltip}
      additionalParams={{
        status: 1,
        ...(societyId ? { societyId } : {}),
        excludeWithDeliveryProof: true,
        take: 50,
      }}
      transformResponse={transformResponse}
      renderOption={(item) => {
        if (item.kind === 'contract') {
          const c = item.data;
          const beneficiaryName = c.beneficiary
            ? `${c.beneficiary.firstName} ${c.beneficiary.lastName}`
            : '';
          const vehicleInfo = [c.vehicle?.registration, [c.vehicle?.brand, c.vehicle?.model].filter(Boolean).join(' ')].filter(Boolean).join(' - ');

          return (
            <div>
              <div className="tw-font-medium tw-text-gray-900">
                {[beneficiaryName, vehicleInfo].filter(Boolean).join(' - ') || c.label}
              </div>
              <div className="tw-text-xs tw-text-gray-500 tw-mt-0.5">
                {c.label}
              </div>
            </div>
          );
        }

        const d = item.data;
        const beneficiary = d.formData?.beneficiary;
        const beneficiaryName = beneficiary
          ? [beneficiary.firstName, beneficiary.lastName].filter(Boolean).join(' ')
          : '';
        const vehicle = d.formData?.vehicle;
        const vehicleInfo = vehicle
          ? [vehicle.brand, vehicle.model].filter(Boolean).join(' ')
          : '';

        return (
          <div>
            <div className="tw-flex tw-items-center tw-gap-2">
              <PenLine className="tw-w-3.5 tw-h-3.5 tw-text-amber-600" />
              <span className="tw-font-medium tw-text-gray-900">
                {d.label || 'Brouillon sans nom'}
              </span>
              <span className="tw-text-xs tw-px-2 tw-py-0.5 tw-rounded-full tw-bg-amber-100 tw-text-amber-700">
                Brouillon
              </span>
            </div>
            {beneficiaryName && (
              <div className="tw-text-sm tw-text-gray-600 tw-mt-1">{beneficiaryName}</div>
            )}
            {vehicleInfo && (
              <div className="tw-text-xs tw-text-gray-500 tw-mt-0.5">{vehicleInfo}</div>
            )}
          </div>
        );
      }}
    />
    {error && <p className="tw-mt-2 tw-text-sm tw-text-red-600">{error}</p>}
    </div>
  );
};
